import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../../contexts/AuthContext';
import { validateEmail } from '../../utils/validation';
import { API_ENDPOINTS } from '../../config/api';
import { authUtils } from '../../utils/auth';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginAttempts, incrementLoginAttempts } = useAuth();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  // Check for existing valid token on component mount
  useEffect(() => {
    const checkExistingToken = async () => {
      const existingToken = authUtils.getToken();

      if (existingToken) {
        try {
          const response = await axios.post(API_ENDPOINTS.AUTH.VERIFY_TOKEN, {
            token: existingToken
          });

          if (response.data.success && response.data.email) {
            // Token is still valid, update auth context and redirect to chat
            login(existingToken, response.data.name || response.data.email);
            authUtils.setEmail(response.data.email);
            navigate('/chat', { replace: true });
          } else {
            // Token is invalid, clear it
            authUtils.clearAll();
          }
        } catch (err) {
          // Token verification failed, clear it
          authUtils.clearAll();
        }
      }

      setCheckingAuth(false);
    };

    checkExistingToken();
  }, [navigate]);

  useEffect(() => {
    // Check if there's a token in the URL from LinkedIn OAuth
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');

    if (token) {
      // Verify the token first
      setLoading(true);
      axios.post(API_ENDPOINTS.AUTH.VERIFY_TOKEN, { token })
        .then(response => {
          if (response.data.success && response.data.email) {
            // Token is valid, log the user in
            login(token, response.data.name || response.data.email);
            // Also save the email
            authUtils.setEmail(response.data.email);
            // Remove token from URL and navigate to chat
            navigate('/chat', { replace: true });
          } else {
            setError('Invalid or expired LinkedIn login token. Please try again.');
            // Remove token from URL
            navigate('/login', { replace: true });
          }
        })
        .catch(err => {
          console.error('Token verification failed:', err);
          setError('LinkedIn login failed. Please try again.');
          // Remove token from URL
          navigate('/login', { replace: true });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [location.search, login, navigate]);

  useEffect(() => {
    if (loginAttempts >= 3) {
      navigate('/forgot-password');
    }
  }, [loginAttempts, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    const recaptchaToken = await recaptchaRef.current?.executeAsync();
    recaptchaRef.current?.reset();
    if (!recaptchaToken) {
      setError('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(API_ENDPOINTS.AUTH.LOGIN, {
        email,
        password,
        recaptchaToken,
      });

      // Handle successful login
      if (response.status === 200) {
        if (response.data.token) {
          login(response.data.token, response.data.name || email);
          authUtils.setEmail(email);
          navigate('/chat');
        } else if (response.data.success && response.data.data?.token) {
          // Alternative response structure
          login(response.data.data.token, response.data.data.name || email);
          authUtils.setEmail(email);
          navigate('/chat');
        } else {
          setError('Login successful but no token received. Please try again.');
        }
      }
    } catch (err: any) {
      incrementLoginAttempts();

      // Better error handling
      if (err.response?.status === 405) {
        setError('Service temporarily unavailable. Please try again later.');
      } else if (err.response?.status === 401) {
        setError('Invalid email or password. Please check your credentials.');
      } else if (err.response?.status === 403) {
        setError('Account access denied. Please contact support.');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(`Connection error: ${err.message}`);
      } else {
        setError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLinkedInLogin = () => {
    // Redirect to LinkedIn OAuth endpoint
    window.location.href = `${API_ENDPOINTS.AUTH.LINKEDIN_LOGIN}`;
  };

  const handleGoogleLogin = () => {
    // Redirect to Google OAuth endpoint
    window.location.href = `${API_ENDPOINTS.AUTH.GOOGLE_LOGIN}`;
  };

  // Show loading state while checking authentication
  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-jobesta-gray-20">
        <div className="text-center">
          <div className="text-jobesta-gray-60">Checking authentication...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-jobesta-gray-20 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-jobesta shadow-jobesta-card">
        <div>
          <h1 className="text-center text-3xl mb-2 tracking-tight">
            <span className="font-black text-jobesta-blue">Job</span>
            <span className="font-extralight text-jobesta-blue">esta</span>
          </h1>
          <h2 className="text-center text-xl font-semibold text-jobesta-gray-90">
            Sign in to your account
          </h2>
        </div>
        <form className="mt-8 space-y-6 ph-no-capture" onSubmit={handleSubmit}>
          <input type="hidden" name="remember" value="true" />
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-jobesta-gray-40 placeholder-jobesta-gray-60 text-jobesta-gray-90 rounded-t-md focus:outline-none focus:ring-2 focus:ring-jobesta-lightblue focus:ring-opacity-30 focus:border-jobesta-blue focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-jobesta-gray-40 placeholder-jobesta-gray-60 text-jobesta-gray-90 rounded-b-md focus:outline-none focus:ring-2 focus:ring-jobesta-lightblue focus:ring-opacity-30 focus:border-jobesta-blue focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-jobesta bg-jobesta-red bg-opacity-10 p-4 border border-jobesta-red">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-jobesta-red">{error}</h3>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <Link
              to="/forgot-password"
              className="text-sm text-jobesta-blue hover:text-jobesta-darkblue hover:underline transition-colors"
            >
              Forgot your password?
            </Link>
            <Link
              to="/register"
              className="text-sm text-jobesta-blue hover:text-jobesta-darkblue hover:underline transition-colors"
            >
              Create an account
            </Link>
          </div>

          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
            size="invisible"
          />

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-semibold rounded-jobesta text-white bg-jobesta-blue hover:bg-jobesta-darkblue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jobesta-lightblue focus:ring-opacity-30 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-jobesta-gray-40" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-jobesta-gray-60">Or continue with</span>
            </div>
          </div>

          <div>
            <button
              type="button"
              onClick={handleLinkedInLogin}
              disabled={loading}
              className="group relative w-full flex justify-center items-center py-2 px-4 border border-jobesta-gray-40 text-sm font-semibold rounded-jobesta text-jobesta-gray-90 bg-white hover:bg-jobesta-gray-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jobesta-lightblue focus:ring-opacity-30 disabled:opacity-50 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" fill="#0a66c2"/>
              </svg>
              Sign in with LinkedIn
            </button>
          </div>

          <div>
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="group relative w-full flex justify-center items-center py-2 px-4 border border-jobesta-gray-40 text-sm font-semibold rounded-jobesta text-jobesta-gray-90 bg-white hover:bg-jobesta-gray-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jobesta-lightblue focus:ring-opacity-30 disabled:opacity-50 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Sign in with Google
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;